import { useMutation } from "@tanstack/react-query";

import { createApiClient } from "@/config/createApiClient";
import { download } from "@/helpers/download";
import {
  FileApi,
  type FilesControllerUploadFileUploadKeyEnum,
} from "../../config/apiClient";

const apiClient = createApiClient(FileApi);

/**
 * Safely converts unknown response to string
 */
const parseUploadResponse = (response: unknown): string => {
  if (typeof response === "string") {
    return response;
  }
  if (response && typeof response === "object" && "url" in response) {
    const obj = response as Record<string, unknown>;
    if (typeof obj.url === "string") {
      return obj.url;
    }
  }
  return JSON.stringify(response);
};

const uploadWithProgress = (
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress: (progress: number) => void
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open("PUT", url, true);

    Object.keys(headers).forEach((key) => {
      xhr.setRequestHeader(key, headers[key]);
    });

    xhr.upload.onprogress = (event: ProgressEvent) => {
      if (event.lengthComputable) {
        const percentComplete = (event.loaded / event.total) * 100;
        onProgress(percentComplete);
      }
    };

    // Response management
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parseUploadResponse(xhr.response));
      } else {
        reject(new Error(`Error: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error("Upload interrupted or aborted"));

    xhr.send(file);
  });
};

const useCreateFileUploadUrl = () =>
  useMutation({
    mutationFn: ({
      key,
      ...body
    }: {
      key: string;
      fileName: string;
      size: number;
      contentType: string;
      hash: string;
    }) =>
      apiClient.filesControllerUploadFile({
        uploadKey: key as FilesControllerUploadFileUploadKeyEnum,
        fileUploadCommand: body,
      }),
  });

const useUploadFileToGCS = () =>
  useMutation({
    mutationFn: (params: {
      url: string;
      file: File;
      headers: Record<string, string>;
      onProgress: (progress: number) => void;
    }) =>
      uploadWithProgress(
        params.url,
        params.file,
        params.headers,
        params.onProgress
      ),
  });

const useDownloadFile = () =>
  useMutation({
    mutationFn: async (params: { url: string }) => {
      const response = await apiClient.filesControllerDownloadFile({
        url: params.url,
      });

      const data = String(response.data);
      return data;
    },
  });

const useDownloadFileHandler = () => {
  const { mutateAsync: downloadFile } = useDownloadFile();

  const handleDownloadFile = async (fileName?: string, fileUrl?: string) => {
    if (!fileName || !fileUrl) {
      return;
    }

    const b64string = await downloadFile({ url: fileUrl });
    download(b64string, fileName);
  };

  return {
    handleDownloadFile,
  };
};

export const useFile = () => {
  return {
    useCreateFileUploadUrl,
    useUploadFileToGCS,
    useDownloadFile,
    useDownloadFileHandler,
  };
};
